import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats, testimonials } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '糖心传媒提供哪些服务？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '糖心传媒提供短视频制作、社交媒体运营、KOL合作及全案策划等一站式品牌内容营销服务，覆盖品牌内容创作全链路。',
          },
        },
        {
          '@type': 'Question',
          name: '糖心传媒服务过哪些品牌？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '糖心传媒累计服务品牌超过500家，涵盖美妆、餐饮、科技、运动、母婴等多个行业，合作伙伴包括欧莱雅、完美日记、海底捞、喜茶、小米、安踏等知名品牌。',
          },
        },
        {
          '@type': 'Question',
          name: '如何与糖心传媒合作？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '您可以通过官网提交合作咨询表单，或致电400-888-9999，我们的商务团队将在24小时内与您联系，了解品牌需求并制定合作方案。',
          },
        },
        {
          '@type': 'Question',
          name: '糖心传媒的服务流程是怎样的？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '糖心传媒采用五步标准化服务流程：需求诊断、策略规划、创意执行、数据优化、复盘增长。每个环节都有严格的质量把控，确保品牌内容策略的精准落地。',
          },
        },
      ],
    };

    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(el);

    return () => {
      document.head.removeChild(el);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9tcmj7n8bpodh48r72g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <section className="pt-24 md:pt-28 pb-8 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-6">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
                关于糖心传媒
              </h1>
              <p className="text-foreground-600 text-base md:text-lg max-w-3xl leading-relaxed">
                糖心传媒成立于2018年，是专注品牌内容创作与数字营销的专业传媒机构。我们相信，好的内容自带传播力，而专业的数据驱动能让传播更精准、更可度量。
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20team%20collaborating%20in%20a%20bright%20open%20office%20space%2C%20warm%20natural%20lighting%2C%20minimalist%20Scandinavian%20design%2C%20diverse%20professionals%20working%20together%2C%20creative%20agency%20atmosphere%2C%20casual%20professional%20style&width=800&height=600&seq=about-team-tangxin&orientation=landscape"
                    alt="糖心传媒团队"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div>
                <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                  品牌故事
                </span>
                <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-5">
                  用内容连接品牌与用户
                </h2>
                <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                  <p>
                    糖心传媒由一群热爱内容创作的营销人于2018年在上海创立。创始团队来自头部互联网平台与4A广告公司，对内容营销有着深刻的理解与丰富的实战经验。
                  </p>
                  <p>
                    我们服务的客户覆盖美妆、餐饮、科技、运动、母婴等多个行业，累计服务品牌超过500家。从初创品牌到行业龙头，我们为不同阶段的品牌提供定制化的内容营销解决方案。
                  </p>
                  <p>
                    在内容营销领域，糖心传媒率先提出「数据驱动的内容策略」方法论，将数据分析与创意内容深度融合，让每一次内容触达都可追踪、可度量、可优化。
                  </p>
                  <p>
                    2024年，糖心传媒获得「年度最具影响力数字营销机构」称号，旗下多个案例获得行业权威奖项认可。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-2">
                    {stat.value}
                    <span className="text-lg md:text-xl text-background-50/70">{stat.suffix}</span>
                  </div>
                  <p className="text-background-50/75 text-sm md:text-base">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                行业口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">客户眼中的糖心传媒</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((item) => (
                <div key={item.name} className="bg-background-100/70 rounded-2xl p-6 md:p-7">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-5">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block bg-secondary-100 text-secondary-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  开启品牌内容增长之旅
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  无论您是初创品牌还是行业龙头，糖心传媒都能为您量身定制内容营销方案
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                <div className="bg-background-50 rounded-2xl p-6 text-center">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-950 mb-1">商务合作</p>
                  <p className="text-xs text-foreground-500">hello@tangxinmedia.com</p>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 text-center">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-phone-line text-lg text-accent-600"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-950 mb-1">电话咨询</p>
                  <p className="text-xs text-foreground-500">400-888-9999</p>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 text-center">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-950 mb-1">公司地址</p>
                  <p className="text-xs text-foreground-500">上海市静安区南京西路1788号</p>
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-2xl text-primary-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950 mb-2">提交成功！</h3>
                    <p className="text-foreground-600 text-sm">感谢您的咨询，我们将在24小时内与您联系。</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          公司名称
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入公司名称"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电话
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的电话"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        感兴趣的服务
                      </label>
                      <select
                        id="service"
                        name="service"
                        className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors appearance-none"
                      >
                        <option value="">请选择服务类型</option>
                        <option value="short-video">短视频制作</option>
                        <option value="social-media">社交媒体运营</option>
                        <option value="kol">KOL合作</option>
                        <option value="full-case">全案策划</option>
                        <option value="other">其他</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        需求描述
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请简要描述您的品牌需求（500字以内）"
                      ></textarea>
                    </div>

                    <div className="honeypot-field" aria-hidden="true">
                      <label htmlFor="phone_alt">Phone Alt</label>
                      <input
                        type="text"
                        id="phone_alt"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                        aria-hidden="true"
                      />
                    </div>

                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-xl">{formError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 py-3.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '提交咨询'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}